import { useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Copy, Download, Share2, Users, Wallet, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, zmw, dt } from "@/lib/api";
import { PageHeader, Card, Stat, Loading, StatusBadge, Table, Note, useLoad, act } from "@/components/common";
import { useAuth } from "@/context/Auth";

function LinkCard({ link }) {
  const download = () => {
    const c = document.getElementById("ref-qr");
    const a = document.createElement("a");
    a.href = c.toDataURL("image/png"); a.download = "tiyeni-invite-qr.png"; a.click();
  };
  const msg = `Join me on Tiyeni Capital — save, invest or borrow with clear records. Sign up here: ${link}`;
  return (
    <Card title="Your personal invite link" testid="ref-link-card">
      <div className="grid gap-6 md:grid-cols-[auto_1fr] md:items-center">
        <div className="mx-auto rounded-2xl border-4 border-[#0369a1] bg-white p-3"><QRCodeCanvas id="ref-qr" value={link} size={180} includeMargin data-testid="ref-qr" /></div>
        <div className="space-y-3">
          <p className="text-sm text-stone-600">Share this link or let people scan the QR code. Anyone who signs up through it is saved as <b>your client</b>, and you earn a commission every time the company makes money from them.</p>
          <div className="flex gap-2"><Input readOnly value={link} className="font-mono text-xs" data-testid="ref-link" />
            <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link copied"); }} data-testid="ref-copy"><Copy className="h-4 w-4" /></Button></div>
          <div className="flex flex-wrap gap-2">
            <Button asChild className="bg-emerald-600 hover:bg-emerald-700" data-testid="ref-whatsapp"><a href={`https://wa.me/?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer"><Share2 className="mr-1.5 h-4 w-4" />Share on WhatsApp</a></Button>
            <Button variant="outline" onClick={download} data-testid="ref-qr-download"><Download className="mr-1.5 h-4 w-4" />Download QR for posters</Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

function AgentsPayout() {
  const { data, reload } = useLoad("/staff/commissions");
  const [ref, setRef] = useState({});
  if (!data) return null;
  return (
    <Card title="All agents — commissions to pay" tip="Agents earn a share of the company's income from clients they brought in. Pay them by MoMo, then enter the transaction ID here." testid="agents-payout">
      <Table rows={data.map((a) => ({ ...a, id: a.agent_id }))} empty="No commissions earned yet." cols={[
        { k: "name", h: "Agent", r: (a) => <div><b>{a.name}</b><div className="text-xs text-stone-500">{a.phone}</div></div> }, { k: "clients", h: "Clients" },
        { k: "earned", h: "Earned (all time)", right: true, r: (a) => zmw(a.earned) }, { k: "paid", h: "Paid", right: true, r: (a) => zmw(a.paid) },
        { k: "unpaid", h: "To pay now", right: true, r: (a) => <b className="font-mono">{zmw(a.unpaid)}</b> },
        { k: "x", h: "", r: (a) => a.unpaid > 0 && <div className="flex gap-1"><Input placeholder="MoMo transaction ID" className="h-8 w-40" value={ref[a.agent_id] || ""} onChange={(e) => setRef({ ...ref, [a.agent_id]: e.target.value })} data-testid={`pay-agent-ref-${a.agent_id}`} />
          <Button size="sm" data-testid={`pay-agent-${a.agent_id}`} onClick={async () => { if (await act(() => api.post(`/staff/commissions/${a.agent_id}/pay`, { reference: ref[a.agent_id] || "" }), "Commission marked as paid")) reload(); }}>Mark paid</Button></div> }]} />
    </Card>
  );
}

export default function Referrals() {
  const { can } = useAuth();
  const { data } = useLoad("/referrals/mine");
  if (!data) return <Loading />;
  const link = `${window.location.origin}/apply?ref=${data.code}`;
  return (
    <div className="space-y-6">
      <PageHeader overline="Grow Tiyeni" title="Invite & earn" explain={`Your job: find investors and borrowers. Share your link or QR code. Everyone who joins through it becomes your client. You earn ${data.rate_pct}% of what the company earns from them — for example, if the company earns K1,000 in interest and fees from your clients, you get K${(Number(data.rate_pct) * 10).toFixed(0)}.`} />
      {!data.enabled && <Note tone="warn">Commissions are switched off in Settings right now.</Note>}
      <LinkCard link={link} />
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat tone="dark" label="My clients" value={String(data.clients.length)} hint="People who joined with your link" testid="ref-clients-count" />
        <Stat tone="gold" label="Commission waiting to be paid" value={data.totals.unpaid} testid="ref-unpaid" />
        <Stat label="Commission already paid to me" value={data.totals.paid} testid="ref-paid" />
      </div>
      <Card title="My clients" testid="ref-clients">
        <Table rows={data.clients} empty="Nobody has joined with your link yet. Share it on WhatsApp to get started!" cols={[
          { k: "full_name", h: "Name", r: (c) => <span className="inline-flex items-center gap-2"><Users className="h-4 w-4 text-stone-400" />{c.full_name}</span> },
          { k: "created_at", h: "Joined", r: (c) => dt(c.created_at) }, { k: "status", h: "Status", r: (c) => <StatusBadge status={c.status} /> },
          { k: "commission_minor", h: "I earned from them", right: true, r: (c) => zmw(c.commission_minor) }]} />
      </Card>
      <Card title="My commission history" testid="ref-history">
        <Table rows={data.commissions} empty="No commission yet." cols={[{ k: "created_at", h: "Date", r: (m) => dt(m.created_at) }, { k: "client_name", h: "Client" },
          { k: "source", h: "From", r: (m) => ({ loan_repayment: "Loan repayment", withdrawal_fee: "Withdrawal fee", group_loan_fee: "Village loan fee" }[m.source] || m.source) },
          { k: "amount_minor", h: "Amount", right: true, r: (m) => zmw(m.amount_minor) }, { k: "status", h: "", r: (m) => m.status === "paid" ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" />Paid</span> : <span className="inline-flex items-center gap-1 text-xs text-amber-700"><Wallet className="h-3.5 w-3.5" />Waiting</span> }]} />
      </Card>
      {can("view_reports") && <AgentsPayout />}
    </div>
  );
}
